import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import toast from 'react-hot-toast';

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ||
  `${import.meta.env.VITE_API_URL || 'http://localhost:8000'}${import.meta.env.VITE_API_BASE_PATH || '/api'}`;

const INACTIVITY_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour

// --- Inactivity tracker ---
let inactivityTimer: ReturnType<typeof setTimeout> | null = null;

function forceLogout() {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('auth-storage');
  toast.error('Session expired due to inactivity. Please log in again.');
  window.location.href = '/login';
}

function resetInactivityTimer() {
  if (inactivityTimer) clearTimeout(inactivityTimer);
  if (localStorage.getItem('auth_token')) {
    inactivityTimer = setTimeout(forceLogout, INACTIVITY_TIMEOUT_MS);
  }
}

// Track user activity
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'];
ACTIVITY_EVENTS.forEach((event) => {
  window.addEventListener(event, resetInactivityTimer, { passive: true });
});

// Start the timer on load if logged in
resetInactivityTimer();

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - add auth token + reset inactivity timer
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    resetInactivityTimer();
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle errors
let isRefreshing = false;

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized
    if (error.response?.status === 401 && originalRequest) {
      const refreshToken = localStorage.getItem('refresh_token');

      if (refreshToken && !isRefreshing) {
        isRefreshing = true;
        try {
          const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const { token } = response.data;
          localStorage.setItem('auth_token', token);
          if (response.data.refreshToken) {
            localStorage.setItem('refresh_token', response.data.refreshToken);
          }
          // Update currency in persisted auth store if changed
          if (response.data.currency) {
            try {
              const raw = localStorage.getItem('auth-storage');
              if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed?.state?.currency !== response.data.currency) {
                  parsed.state.currency = response.data.currency;
                  localStorage.setItem('auth-storage', JSON.stringify(parsed));
                }
              }
            } catch { /* ignore parse errors */ }
          }
          resetInactivityTimer();

          // Retry original request with new token
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }
          return apiClient(originalRequest);
        } catch {
          forceLogout();
          return Promise.reject(error);
        } finally {
          isRefreshing = false;
        }
      } else if (!refreshToken) {
        forceLogout();
      }
    }

    // Handle other errors
    const detail = (error.response?.data as any)?.detail;
    const message =
      (error.response?.data as any)?.message ||
      (typeof detail === 'string' ? detail : null) ||
      (Array.isArray(detail) ? detail.map((e: any) => e.msg).join(', ') : null) ||
      error.message ||
      'An unexpected error occurred';

    const silentErrors = [401, 404];
    if (!silentErrors.includes(error.response?.status || 0)) {
      toast.error(message);
    }

    return Promise.reject(error);
  }
);

// Helper functions for common HTTP methods
export const api = {
  get: <T>(url: string, config?: any) =>
    apiClient.get<T>(url, config).then((res) => res.data),

  post: <T>(url: string, data?: any, config?: any) =>
    apiClient.post<T>(url, data, config).then((res) => res.data),

  put: <T>(url: string, data?: any, config?: any) =>
    apiClient.put<T>(url, data, config).then((res) => res.data),

  patch: <T>(url: string, data?: any, config?: any) =>
    apiClient.patch<T>(url, data, config).then((res) => res.data),

  delete: <T>(url: string, config?: any) =>
    apiClient.delete<T>(url, config).then((res) => res.data),
};
